/**
 * Woods, generated from a seed.
 *
 * A wood is fifty trees. Written out as fifty literals it is unreadable, it is
 * never edited again because nobody wants to renumber it, and every review of
 * the zone file scrolls past it. Written as a call with a centre and a radius
 * it stays legible, and the trees still land in exactly the same places every
 * time — which matters, because the server and the client both read this data
 * and they have to agree about where the world is.
 *
 * Deterministic, therefore: no `Math.random` anywhere in here.
 */
/** True when a point is inside a building's footprint, with room to spare. */
function insideBuilding(x, z, b, margin) {
    const dx = x - b.position[0];
    const dz = z - b.position[1];
    const rotation = b.rotation ?? 0;
    const sin = Math.sin(-rotation);
    const cos = Math.cos(-rotation);
    const localX = Math.abs(dx * cos - dz * sin);
    const localZ = Math.abs(dx * sin + dz * cos);
    return localX <= b.size[0] / 2 + margin && localZ <= b.size[1] / 2 + margin;
}
/** Small integer hash, so a wood is the same wood on every machine. */
function seeded(seed) {
    let state = (seed >>> 0) || 1;
    return () => {
        state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
        return state / 4294967296;
    };
}
/** Clearance kept between a trunk and a wall, in metres. */
const BUILDING_MARGIN = 2.5;
/** Attempts per tree before giving up on it. */
const TRIES = 12;
const DEFAULT_MIX = [
    'tree', 'tree', 'tree', 'tree', 'tree',
    'pine', 'pine',
    'bush', 'bush', 'bush',
    'deadtree',
    'stump'
];
/**
 * Plants a wood in a ring around a point.
 *
 * Ids are `${prefix}-${n}`, so two woods in the same zone must be given
 * different prefixes — the zone validator rejects duplicate prop ids, which is
 * the check that catches a copy-pasted call.
 */
export function wood(prefix, options) {
    const rand = seeded(options.seed);
    const inner = options.innerRadius ?? 0;
    const spread = options.outerRadius - inner;
    const mix = options.mix ?? DEFAULT_MIX;
    const sizing = options.scale ?? 1;
    const props = [];
    for (let i = 0; i < options.count; i += 1) {
        let position = null;
        /* Try a few spots and take the first that is neither in a building nor off
           the map. Giving up after a dozen goes rather than looping forever matters:
           a wood asked to fill a space that is entirely built on would otherwise
           hang the module at import time, which is a very bad way to find out. */
        for (let attempt = 0; attempt < TRIES && position === null; attempt += 1) {
            const angle = rand() * Math.PI * 2;
            // sqrt spreads them evenly over the area instead of crowding the inner edge.
            const distance = inner + Math.sqrt(rand()) * spread;
            const x = Math.round((options.centre[0] + Math.cos(angle) * distance) * 10) / 10;
            const z = Math.round((options.centre[1] + Math.sin(angle) * distance) * 10) / 10;
            if (options.bounds !== undefined) {
                if (Math.abs(x) > options.bounds[0] || Math.abs(z) > options.bounds[1])
                    continue;
            }
            if (options.avoid?.some((b) => insideBuilding(x, z, b, BUILDING_MARGIN)) === true)
                continue;
            position = [x, z];
        }
        // Somewhere that crowded is not somewhere a tree belongs. Skipping keeps
        // the ids contiguous-ish and the wood a little thinner, which is fine.
        if (position === null)
            continue;
        const kind = mix[Math.floor(rand() * mix.length)] ?? 'tree';
        // Bushes and stumps are small whatever the wood is; trees vary a lot, which
        // is most of what stops a wood looking like an orchard.
        const base = kind === 'bush' || kind === 'stump' || kind === 'reeds'
            ? 0.8 + rand() * 0.5
            : 0.75 + rand() * 0.7;
        props.push({
            id: `${prefix}-${i + 1}`,
            kind,
            position,
            rotation: Math.round(rand() * 628) / 100,
            scale: Math.round(base * sizing * 100) / 100
        });
    }
    return props;
}
//# sourceMappingURL=scatter.js.map